import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import { useSnackbar } from "notistack";
import {
  getEmailPreferencesApi,
  updateEmailPreferencesApi,
} from "src/DAL/Profile/Profile";

/**
 * Profile → Email preferences: which emails this coach receives. A change is saved straight
 * away (no Update button). First item: athlete comment notifications (default On); the in-app
 * notification is sent either way.
 */
export default function EmailPreferences() {
  const { enqueueSnackbar } = useSnackbar();
  const [athleteComments, setAthleteComments] = useState(null); // null while loading
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    getEmailPreferencesApi()
      .then((res) => {
        if (!alive) return;
        // Not loaded → shown as On (the default) so the switch is still usable.
        setAthleteComments(
          res?.code === 200
            ? res.email_preferences?.athlete_comment_emails !== false
            : true
        );
      })
      .catch(() => alive && setAthleteComments(true));
    return () => {
      alive = false;
    };
  }, []);

  const handleChange = async (event) => {
    const next = event.target.checked;
    const previous = athleteComments;
    setAthleteComments(next);
    setSaving(true);
    const res = await updateEmailPreferencesApi({ athlete_comment_emails: next });
    setSaving(false);
    if (res?.code === 200) {
      enqueueSnackbar(res.message || "Email preferences saved", {
        variant: "success",
      });
    } else {
      setAthleteComments(previous);
      enqueueSnackbar(res?.message || "Could not save email preferences", {
        variant: "error",
      });
    }
  };

  return (
    // Same card as the profile form above it.
    <div className="mb-4 d-flex justify-content-center">
      <div className="cards-edit p-4">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              id="athlete-comment-emails-label"
              sx={{ fontWeight: 600, fontSize: 16 }}
            >
              Athlete comment emails
            </Typography>
            <Typography sx={{ fontSize: 13.5, opacity: 0.7, mt: 0.5 }}>
              Get an email when your athletes comment on a workout. If you turn
              this off, you will still see their comments in your
              notifications.
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              minWidth: 58,
            }}
          >
            {athleteComments === null ? (
              <CircularProgress size={22} />
            ) : (
              <Switch
                checked={athleteComments}
                onChange={handleChange}
                disabled={saving}
                inputProps={{ "aria-labelledby": "athlete-comment-emails-label" }}
              />
            )}
          </Box>
        </Box>
      </div>
    </div>
  );
}
